import type { TFunction } from 'i18next'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { AmountLine } from '@/ds'
import { useLoadedStay, useStay } from '@/app/stay/StayProvider'
import { dayMonth, dayMonthLong, formatMoney, listJoin, time } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import type { ChangeInput, ChangeOptionType, ChangeQuote, ChangeResult, ChangeType, Stay } from '@/services/reserva'
import '../Pagos/Pagos.css'
import './Reserva.css'

/** Rutas del bloque 5 (inventario D16, ampliado: un tramo por tipo de cambio). */
export const SLUG: Record<ChangeOptionType, string> = {
  dates: 'fechas',
  nights: 'noches',
  guests: 'personas',
  unit: 'alojamiento',
  early_checkin: 'entrar-antes',
  late_checkout: 'salir-mas-tarde',
  cancel: 'cancelar',
}
export const TYPE_BY_SLUG: Record<string, ChangeType> = { fechas: 'dates', noches: 'nights', personas: 'guests', alojamiento: 'unit', 'entrar-antes': 'early_checkin', 'salir-mas-tarde': 'late_checkout' }

export const BOOKING_ROUTES = {
  detail: '/reserva',
  cancel: '/reserva/cancelar',
  pick: (type: ChangeType) => `/reserva/cambiar/${SLUG[type]}`,
  price: (type: ChangeType) => `/reserva/cambiar/${SLUG[type]}/precio`,
  pay: (type: ChangeType) => `/reserva/cambiar/${SLUG[type]}/pagar`,
  done: (type: ChangeType) => `/reserva/cambiar/${SLUG[type]}/listo`,
  notCharged: (type: ChangeType) => `/reserva/cambiar/${SLUG[type]}/sin-pago`,
  unavailable: (type: ChangeOptionType) => (type === 'cancel' ? '/reserva/cancelar/no-disponible' : `/reserva/cambiar/${SLUG[type]}/no-disponible`),
} as const

/**
 * Pantalla desde la que se entra a un cambio de reserva (D47): el flujo vuelve a ella al ir atrás o al terminar.
 * Viaja en la query como `volver=<origen>` (se conserva entre pasos y en la autorización del banco) y se quita al
 * salir. Solo se aceptan estos orígenes, nunca una ruta libre.
 */
export type ChangeOrigin = 'reserva' | 'extras' | 'peticion' | 'checkin' | 'salida' | 'estancia' | 'accesos' | 'despues'
export const RETURN_PARAM = 'volver'
export const ORIGIN_PATHS: Record<ChangeOrigin, string> = {
  reserva: '/reserva',
  extras: '/reserva/extras',
  peticion: '/ayuda/peticiones/nueva',
  checkin: '/checkin/paso/6',
  salida: '/salida',
  // Pago pendiente y la fianza usan el mismo parámetro de retorno (fase 4, B4).
  estancia: '/',
  accesos: '/accesos',
  despues: '/salida/despues',
}

export const changeQuery = (origin: ChangeOrigin) => ({ [RETURN_PARAM]: origin })

export function readOrigin(search: string): ChangeOrigin {
  const value = new URLSearchParams(search).get(RETURN_PARAM)
  return value && value in ORIGIN_PATHS ? (value as ChangeOrigin) : 'reserva'
}

/** Salida del flujo de cambios, de Pago pendiente y de la fianza: al origen, con su etiqueta, y sin el parámetro de retorno. */
export function useChangeExit() {
  const { t } = useTranslation()
  const { search } = useLocation()
  const { go } = useAppNavigate()
  const origin = readOrigin(search)
  return {
    origin,
    path: ORIGIN_PATHS[origin],
    backLabel: t(`v2.reserva.origen.${origin}.atras`),
    returnLabel: t(`v2.reserva.origen.${origin}.volver`),
    exit: (options: { replace?: boolean } = {}) => go(ORIGIN_PATHS[origin], { ...options, query: { [RETURN_PARAM]: null } }),
  }
}

/** Secciones del detalle a las que enlazan otras pantallas (Tu estancia): `/reserva#documentos`. */
export const BOOKING_SECTIONS = { dates: 'fechas', payments: 'pagos', policy: 'politica', occupants: 'ocupantes', documents: 'documentos', changes: 'cambios' } as const

/** Por qué no se puede hacer un cambio desde la app (pantalla "No podemos hacer ese cambio", §5g). */
export type UnavailableReason = 'busy' | 'rejected' | 'channel' | 'property'

/**
 * Borrador del cambio en curso, en la pestaña: elegir → precio → pagar → listo. Si se recarga la página en un paso
 * posterior sin borrador, la pantalla vuelve al paso 1.
 */
export interface ChangeDraft {
  guestCode: string
  type: ChangeOptionType
  input: ChangeInput | null
  quote: ChangeQuote | null
  reason: UnavailableReason | null
}

const DRAFT_KEY = 'v2.reserva.cambio'

export function readDraft(guestCode: string, type: ChangeOptionType): ChangeDraft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY)
    const draft = raw ? (JSON.parse(raw) as ChangeDraft) : null
    return draft && draft.guestCode === guestCode && draft.type === type ? draft : null
  } catch {
    return null
  }
}

export function writeDraft(draft: ChangeDraft | null): void {
  try {
    if (draft) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    else sessionStorage.removeItem(DRAFT_KEY)
  } catch {
    /* sin almacenamiento */
  }
}

export function useBookingStay(): Stay {
  return useLoadedStay()
}

export function useReplaceStay(): (stay: Stay) => void {
  return useStay().replaceStay
}

/** `yyyy-MM-dd` de un ISO con hora, en la hora local (la del resto de fechas de la app, R13). */
export function dayOf(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** `yyyy-MM-dd` → ISO a mediodía (evita saltos de día al formatear con Intl). */
export const noon = (day: string) => `${day}T12:00:00`

/** "3 – 6 oct" o "30 sep – 2 oct" a partir de días sueltos. */
export function dayRange(from: string, to: string, lang: string): string {
  const a = new Date(noon(from))
  const b = new Date(noon(to))
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
  return `${sameMonth ? String(a.getDate()) : dayMonth(noon(from), lang)} – ${dayMonth(noon(to), lang)}`
}

/** "del 10 al 13 de octubre" (mismo mes) o "del 30 de septiembre al 2 de octubre". */
export function longRange(from: string, to: string, lang: string, t: TFunction): string {
  const a = new Date(noon(from))
  const b = new Date(noon(to))
  const sameMonth = a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
  return t('v2.reserva.rango', { from: sameMonth ? String(a.getDate()) : dayMonthLong(noon(from), lang), to: dayMonthLong(noon(to), lang) })
}

/** "2 adultos y 1 niño" */
export function guestsText(adults: number, children: number, lang: string, t: TFunction): string {
  const parts = [t('v2.reserva.datos.adultos', { count: adults })]
  if (children > 0) parts.push(t('v2.reserva.datos.ninos', { count: children }))
  return listJoin(parts, lang)
}

/** Importe con signo tipográfico: "+30,00 €", "−30,00 €". */
export function signed(amount: ChangeQuote['difference'], lang: string): string {
  const abs = formatMoney({ ...amount, amount: Math.abs(amount.amount) }, lang)
  if (amount.amount > 0) return `+${abs}`
  if (amount.amount < 0) return `−${abs}`
  return abs
}
export const minus = (m: ChangeQuote['difference'], lang: string) => `−${formatMoney({ ...m, amount: Math.abs(m.amount) }, lang)}`

/** Texto corto de lo que cambia, para el concepto del cobro y la petición a la propiedad. */
export function changeSummary(quote: ChangeQuote, stay: Stay, lang: string, t: TFunction): string {
  const { input } = quote
  switch (input.type) {
    case 'dates':
    case 'nights':
      return dayRange(input.checkIn, input.checkOut, lang)
    case 'guests':
      return guestsText(input.adults, input.children, lang, t)
    case 'unit':
      return quote.unitName ?? stay.unit.name
    case 'early_checkin':
    case 'late_checkout':
      return input.time
  }
}

/** Filas del desglose (§5c): lo nuevo, lo ya pagado o el precio actual en negativo, y el coste del cambio. */
export function quoteLines(quote: ChangeQuote, stay: Stay, lang: string, t: TFunction): AmountLine[] {
  const lines: AmountLine[] = []
  const { input } = quote
  const currentRange = dayRange(dayOf(stay.dates.checkIn), dayOf(stay.dates.checkOut), lang)
  if (input.type === 'early_checkin' || input.type === 'late_checkout') {
    lines.push({ label: t(input.type === 'early_checkin' ? 'v2.reserva.precio.lineaEntrada' : 'v2.reserva.precio.lineaSalida', { time: input.time }), amount: formatMoney(quote.difference, lang) })
    return lines
  }
  if (quote.newPrice) {
    let label: string
    if (input.type === 'guests') label = t('v2.reserva.precio.lineaPersonas', { guests: guestsText(input.adults, input.children, lang, t) })
    else if (input.type === 'unit') label = t('v2.reserva.precio.lineaUnidad', { unit: quote.unitName ?? '', range: currentRange })
    else if ('checkIn' in input) label = t('v2.reserva.precio.lineaFechas', { range: dayRange(input.checkIn, input.checkOut, lang), count: quote.nights ?? 0 })
    else label = ''
    lines.push({ label, amount: formatMoney(quote.newPrice, lang) })
  }
  if (quote.alreadyPaid) lines.push({ label: t('v2.reserva.precio.yaPagado', { range: currentRange }), amount: minus(quote.alreadyPaid, lang), tone: 'muted' })
  else if (quote.currentPrice) lines.push({ label: t('v2.reserva.precio.precioActual', { range: currentRange }), amount: minus(quote.currentPrice, lang), tone: 'muted' })
  if (quote.changeFee) {
    lines.push(quote.changeFee.amount === 0 ? { label: t('v2.reserva.precio.coste'), amount: t('v2.reserva.precio.gratis'), tone: 'success' } : { label: t('v2.reserva.precio.coste'), amount: formatMoney(quote.changeFee, lang) })
  }
  return lines
}

/** "Libre del 7 al 10 y del 13 al 16 de octubre" */
export function alternativesText(quote: ChangeQuote, lang: string, t: TFunction): string | null {
  const list = quote.alternatives
  const last = list[list.length - 1]
  if (!last) return null
  const month = (day: string) => day.slice(0, 7)
  const ranges = list.map((a, i) => {
    // Como el bundle: el mes solo al final cuando todos los rangos acaban en el mismo mes.
    if (i < list.length - 1 && month(a.from) === month(a.to) && month(a.to) === month(last.to)) return t('v2.reserva.rango', { from: String(new Date(noon(a.from)).getDate()), to: String(new Date(noon(a.to)).getDate()) })
    return longRange(a.from, a.to, lang, t)
  })
  return t('v2.reserva.noDisponible.libre', { ranges: listJoin(ranges, lang) })
}

export function useLang() {
  const { t, i18n } = useTranslation()
  return { t, lang: i18n.language }
}

export const hourOf = (iso: string, lang: string) => time(iso, lang)

/** Resultado del último cambio aplicado, para la confirmación (paso 4) aunque se recargue la página. */
const RESULT_KEY = 'v2.reserva.resultado'

export function writeResult(guestCode: string, result: ChangeResult | null): void {
  try {
    if (result) sessionStorage.setItem(RESULT_KEY, JSON.stringify({ guestCode, result }))
    else sessionStorage.removeItem(RESULT_KEY)
  } catch {
    /* sin almacenamiento */
  }
}

export function readResult(guestCode: string, type: ChangeType): ChangeResult | null {
  try {
    const raw = sessionStorage.getItem(RESULT_KEY)
    const value = raw ? (JSON.parse(raw) as { guestCode: string; result: ChangeResult }) : null
    return value && value.guestCode === guestCode && value.result.type === type ? value.result : null
  } catch {
    return null
  }
}

/** Pasos del patrón de cambio: elegir, disponibilidad y precio, pagar, confirmación (§5b–5e). */
export const TOTAL_CHANGE_STEPS = 4
